import React, { Fragment, useEffect, useState } from 'react';
import * as HeadlessUI from '@headlessui/react';
import { FaCheckCircle, FaTimesCircle, FaTimes, FaUserSlash } from 'react-icons/fa';
import ConfirmationModal from './ConfirmationModal';
import { createXeroContactsForFranchisees } from '../services/franchiseeService';

const actionLabel = (action) => {
  if (action === 'linked') return 'Linked to existing contact';
  if (action === 'created') return 'New contact created';
  return 'Created or linked';
};

/**
 * Lists active franchisees with no linked Xero contact, then creates/links the ones the admin ticks
 * after a confirmation, showing the successful and unsuccessful results.
 */
const MissingXeroContactsModal = ({ isOpen, onClose, franchisees = [], onCompleted }) => {
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  // Nothing ticked by default: each one becomes a real Xero contact, so the admin picks deliberately.
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  // A fresh open always starts from the list, never the previous run's results.
  useEffect(() => {
    if (isOpen) {
      setConfirming(false);
      setSubmitting(false);
      setResults(null);
      setError(null);
      setSelectedIds(new Set());
    }
  }, [isOpen]);

  const allSelected = franchisees.length > 0 && selectedIds.size === franchisees.length;
  const toggleOne = (id) => setSelectedIds((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const toggleAll = () => setSelectedIds(allSelected ? new Set() : new Set(franchisees.map((f) => f.id)));

  const handleCreate = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const result = await createXeroContactsForFranchisees([...selectedIds]);
      setResults(result);
      if (onCompleted) onCompleted();
    } catch (err) {
      setError(err.message || 'Failed to create Xero contacts.');
    } finally {
      setSubmitting(false);
      setConfirming(false);
    }
  };

  const handleClose = () => {
    if (submitting) return;
    onClose();
  };

  const succeeded = results?.results?.filter((r) => r.status === 'success') || [];
  const failed = results?.results?.filter((r) => r.status !== 'success') || [];

  return (
    <>
      <HeadlessUI.Transition appear show={isOpen} as={Fragment}>
        <HeadlessUI.Dialog onClose={handleClose} className="relative z-50">
          <HeadlessUI.TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
          </HeadlessUI.TransitionChild>

          <div className="fixed inset-0 flex w-screen items-center justify-center p-4">
            <HeadlessUI.TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <HeadlessUI.DialogPanel className="w-full max-w-2xl my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-lg flex flex-col max-h-[90vh]">
                <div className="border-b border-gray-200 px-6 py-4 flex justify-between items-center flex-shrink-0">
                  <HeadlessUI.DialogTitle as="h3" className="text-lg font-semibold text-gray-900">
                    Franchisees not in Xero
                  </HeadlessUI.DialogTitle>
                  <button onClick={handleClose} className="text-gray-400 hover:text-gray-500 focus:outline-none" aria-label="Close">
                    <FaTimes />
                  </button>
                </div>

                <div className="px-6 py-4 overflow-y-auto flex-1 min-h-0">
                  {error && (
                    <div role="alert" className="bg-red-50 border-l-4 border-red-400 text-red-700 text-sm p-3 mb-4 rounded">
                      {error}
                    </div>
                  )}

                  {results ? (
                    <div className="space-y-4">
                      <div className="grid grid-cols-3 gap-3 text-sm">
                        <div className="bg-gray-50 p-3 rounded-lg">
                          <p className="text-gray-700">Processed: {results.totalProcessed}</p>
                        </div>
                        <div className="bg-green-50 p-3 rounded-lg">
                          <p className="text-green-700">Created / linked: {results.totalSuccess}</p>
                        </div>
                        <div className="bg-red-50 p-3 rounded-lg">
                          <p className="text-red-700">Failed: {results.totalFailed}</p>
                        </div>
                      </div>

                      {succeeded.length > 0 && (
                        <div>
                          <h4 className="text-sm font-semibold text-gray-700 mb-2">Successful</h4>
                          <div className="border rounded-lg divide-y">
                            {succeeded.map((item) => (
                              <div key={item.franchiseeId} className="p-3 flex items-start gap-3">
                                <FaCheckCircle className="text-green-500 mt-0.5 shrink-0" />
                                <div>
                                  <p className="font-medium text-gray-800">{item.name}{item.fseCode ? ` (${item.fseCode})` : ''}</p>
                                  <p className="text-sm text-gray-600">{actionLabel(item.action)}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {failed.length > 0 && (
                        <div>
                          <h4 className="text-sm font-semibold text-gray-700 mb-2">Unsuccessful</h4>
                          <div className="border rounded-lg divide-y">
                            {failed.map((item) => (
                              <div key={item.franchiseeId} className="p-3 flex items-start gap-3">
                                <FaTimesCircle className="text-red-500 mt-0.5 shrink-0" />
                                <div>
                                  <p className="font-medium text-gray-800">{item.name}{item.fseCode ? ` (${item.fseCode})` : ''}</p>
                                  <p className="text-sm text-gray-600">{item.message}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : franchisees.length === 0 ? (
                    <div className="flex items-center justify-center p-6 text-gray-600">
                      <FaCheckCircle className="text-green-500 mr-3" />
                      <p>Every active franchisee has a Xero contact.</p>
                    </div>
                  ) : (
                    <div>
                      <p className="text-sm text-gray-600 mb-3">
                        The following active franchisees have no Xero contact. Tick the ones to create in Xero:
                        an existing Xero contact is linked where the FSE code matches, otherwise a new one is created.
                      </p>
                      <label className="flex items-center gap-2 mb-2 text-sm text-gray-700 cursor-pointer">
                        <input type="checkbox" checked={allSelected} onChange={toggleAll} className="h-4 w-4" />
                        Select all ({franchisees.length})
                      </label>
                      <div className="border rounded-lg divide-y max-h-96 overflow-y-auto">
                        {franchisees.map((f) => (
                          <label key={f.id} className="p-3 flex items-center gap-3 cursor-pointer hover:bg-gray-50">
                            <input type="checkbox" checked={selectedIds.has(f.id)} onChange={() => toggleOne(f.id)} className="h-4 w-4 shrink-0" />
                            <FaUserSlash className="text-red-500 shrink-0" />
                            <div>
                              <p className="font-medium text-gray-800">{f.franchiseName || f.id}</p>
                              <p className="text-sm text-gray-500">{f.fseCode ? `FSE ${f.fseCode}` : 'No FSE code'}</p>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3 flex-shrink-0">
                  {results ? (
                    <button
                      onClick={handleClose}
                      className="px-4 py-2 bg-jims-cyan text-white rounded-md hover:bg-jims-cyan-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-jims-cyan"
                    >
                      Done
                    </button>
                  ) : (
                    <>
                      <button
                        onClick={handleClose}
                        className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                      >
                        Close
                      </button>
                      <button
                        onClick={() => setConfirming(true)}
                        disabled={selectedIds.size === 0 || submitting}
                        className="px-4 py-2 bg-jims-cyan text-white rounded-md hover:bg-jims-cyan-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-jims-cyan disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Create in Xero ({selectedIds.size})
                      </button>
                    </>
                  )}
                </div>
              </HeadlessUI.DialogPanel>
            </HeadlessUI.TransitionChild>
          </div>
        </HeadlessUI.Dialog>
      </HeadlessUI.Transition>

      <ConfirmationModal
        isOpen={confirming}
        onClose={() => setConfirming(false)}
        onConfirm={handleCreate}
        title="Create franchisees in Xero?"
        message={`This will create or link Xero contacts for ${selectedIds.size} franchisee(s) in the live Xero account. Continue?`}
        confirmText="Create in Xero"
        loadingText="Creating..."
        isLoading={submitting}
        variant="primary"
      />
    </>
  );
};

export default MissingXeroContactsModal;
